/**
 * MIMU — illustrative perceived-energy comparison, with a guided 6-hour timeline.
 *
 * Adjustable config, in order:
 *   1. SERIES      2. REGIONS/BASELINE   3. ANNOTATIONS
 *   4. TOOLTIPS    5. STORY              6. THEME
 *
 * Y-values are DESIGN values. They are not measurements, and no copy here may
 * describe them as clinical or physiological data.
 */
(function () {
  /* ---------------------------------------------------------------- 1. SERIES */
  var MINUTES = [0, 60, 120, 240, 360];
  var X_LABEL = ['0 min','1 hr','2 hr','4 hr','6 hr'];

  var SERIES = {
    coffee: { label: 'Coffee',                    values: [40, 94, 62, 36, 38] },
    cafe:   { label: 'Cafe matcha + 30g sugar',   values: [40, 78, 43, 29, 31] },
    // MIMU now tracks just under cafe at 1 hr rather than well below it, then holds
    // a broad crest before declining back to the starting baseline.
    mimu:   { label: 'Mimu',                      values: [40, 73, 70, 53, 40] }
  };
  var ORDER = ['mimu', 'coffee', 'cafe'];

  /* ------------------------------------------------------ 2. REGIONS/BASELINE */
  var BASELINE = 40;
  var JITTER_FROM = 84;          // coffee (94) crosses; cafe (78) and mimu (70) stay under
  var Y_MIN = 20, Y_MAX = 100;

  /* ----------------------------------------------------------- 3. ANNOTATIONS */
  // Kept deliberately sparse, desktop-only, and only once the step has reached them.
  var ANNOTATIONS = [
    { series:'coffee', i:1, main:'Caffeine Peak',        sub:'May feel jittery for some', align:'left'  },
    { series:'cafe',   i:2, main:'Potential Energy Dip', sub:'Individual responses vary', align:'left'  },
    { series:'mimu',   i:1, main:'Gradual Energy Lift',  sub:'3 g Matcha + L-Theanine',   align:'right' },
    { series:'mimu',   i:4, main:'Back Toward Baseline', sub:'',                          align:'right' }
  ];

  /* -------------------------------------------------------------- 4. TOOLTIPS */
  var TOOLTIPS = {
    coffee: {
      60:  'Coffee is within the typical caffeine peak-concentration window. Higher caffeine exposure can feel jittery for some individuals.',
      240: 'In this illustrative model, perceived energy has moved below the starting level.',
      _:   'An illustrative view of perceived energy after coffee.'
    },
    cafe: {
      60:  'The combination of caffeine and approximately 30 g of added sugar can create a pronounced early perceived-energy lift.',
      120: 'As blood glucose returns toward baseline after a large sugar load, some people may perceive an energy dip.',
      240: 'As blood glucose returns toward baseline after a large sugar load, some people may perceive an energy dip.',
      _:   'An illustrative view of perceived energy after a sugary café matcha.'
    },
    mimu: {
      60:  'Mimu contains 3 g of matcha with naturally occurring caffeine and L-theanine, MCT powder and 0 g added sugar.',
      120: 'Mimu contains 3 g of matcha with naturally occurring caffeine and L-theanine, MCT powder and 0 g added sugar.',
      360: 'The illustrative curve gradually returns toward the starting energy level.',
      _:   'An illustrative view of perceived energy after Mimu.'
    }
  };

  /* ----------------------------------------------------------------- 5. STORY */
  var STORY = [
    { h:'Your morning starts here', mimu:'3 g matcha • MCT • 0 g added sugar', coffee:'Coffee consumed', cafe:'Matcha + ~30 g added sugar' },
    { h:'Coffee reaches its peak', coffee:'Caffeine is within its typical peak-concentration window. May feel jittery for some.', cafe:'The added-sugar load can contribute to a pronounced early lift, below coffee.', mimu:'Gradual energy lift from 3 g matcha with naturally occurring L-theanine.' },
    { h:'Where the difference becomes clearer', coffee:'Caffeine remains active, although perceived stimulation may begin declining.', cafe:'The early sugar-driven rise has fallen substantially. Potential energy dip.', mimu:'Mimu holds a broad illustrative crest, formulated with 3 g matcha, naturally occurring L-theanine and MCT-derived dietary fat.' },
    { h:'The afternoon difference', coffee:'The illustrative coffee curve now sits slightly below the starting-energy level.', cafe:'The café-matcha curve is deepest within the potential-tiredness region.', mimu:'Mimu remains above its original starting baseline.' },
    { h:'Back toward where you started', mimu:'The illustrative Mimu curve returns approximately to its starting energy level.', coffee:'The coffee curve begins moving back toward baseline.', cafe:'The café-matcha curve remains below its initial level in this conceptual visualization.' }
  ];

  /* ----------------------------------------------------------------- 6. THEME */
  var THEME = {
    mimu:   { colour:'#84B069', width:3.4 },
    coffee: { colour:'#6B5B4F', width:2 },
    cafe:   { colour:'#C2705A', width:2 },
    grid:'rgba(31,27,22,0.07)',
    jitterRGB:'194,112,90',
    tiredRGB:'194,112,90',
    mobileBreak:750,
    duration:800
  };

  /* ------------------------------------------------------------------ ENGINE */
  var root = document.querySelector('[data-mimu-ec]:not([data-ready])');
  if (!root || typeof Chart === 'undefined') return;
  root.setAttribute('data-ready', '');

  var canvas = root.querySelector('[data-mimu-ec-canvas]');
  var elPrev = root.querySelector('[data-ec-prev]');
  var elNext = root.querySelector('[data-ec-next]');
  var elPlay = root.querySelector('[data-ec-play]');
  var elReset = root.querySelector('[data-ec-reset]');
  var elDots = root.querySelector('[data-ec-dots]');
  var elStory = root.querySelector('[data-ec-story]');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var step = 0;          // integer stage 0..8
  var pos = 0;           // animated position, may be fractional
  var raf = null, playing = false, playTimer = null;

  function lerp(a, b, t) { return a + (b - a) * t; }
  function easeInOut(t) { return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2; }

  // Points up to `pos`, with an interpolated head so the line grows smoothly.
  function visible(key) {
    var v = SERIES[key].values, out = [], whole = Math.floor(pos), frac = pos - whole, i;
    for (i = 0; i <= Math.min(whole, MINUTES.length - 1); i++) out.push({ x: MINUTES[i], y: v[i] });
    if (frac > 0 && whole + 1 < MINUTES.length) {
      out.push({ x: lerp(MINUTES[whole], MINUTES[whole+1], frac), y: lerp(v[whole], v[whole+1], frac) });
    }
    return out;
  }

  function radii(key) {
    var pts = visible(key);
    return pts.map(function (_, i) { return i === pts.length - 1 && pts.length > 0 ? 5 : 0; });
  }

  function isNarrow() { return window.innerWidth < THEME.mobileBreak; }

  var decor = {
    id: 'mimuEcDecor',
    beforeDatasetsDraw: function (c) {
      var ctx = c.ctx, y = c.scales.y, x = c.scales.x, a = c.chartArea;
      if (!a) return;
      ctx.save();

      // Upper: higher jitteriness potential.
      var jTop = y.getPixelForValue(Y_MAX), jBot = y.getPixelForValue(JITTER_FROM);
      var g1 = ctx.createLinearGradient(0, jTop, 0, jBot);
      g1.addColorStop(0, 'rgba(' + THEME.jitterRGB + ',0.17)');
      g1.addColorStop(1, 'rgba(' + THEME.jitterRGB + ',0.01)');
      ctx.fillStyle = g1;
      ctx.fillRect(a.left, jTop, a.right - a.left, jBot - jTop);

      // Lower: potential tiredness, below the starting-energy baseline.
      var tTop = y.getPixelForValue(BASELINE), tBot = y.getPixelForValue(Y_MIN);
      var g2 = ctx.createLinearGradient(0, tTop, 0, tBot);
      g2.addColorStop(0, 'rgba(' + THEME.tiredRGB + ',0.02)');
      g2.addColorStop(1, 'rgba(' + THEME.tiredRGB + ',0.14)');
      ctx.fillStyle = g2;
      ctx.fillRect(a.left, tTop, a.right - a.left, tBot - tTop);

      ctx.setLineDash([4,4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(' + THEME.jitterRGB + ',0.4)';
      ctx.beginPath(); ctx.moveTo(a.left, jBot); ctx.lineTo(a.right, jBot); ctx.stroke();

      // Starting-energy baseline.
      ctx.strokeStyle = 'rgba(31,27,22,0.30)';
      ctx.beginPath(); ctx.moveTo(a.left, tTop); ctx.lineTo(a.right, tTop); ctx.stroke();
      ctx.setLineDash([]);

      ctx.textAlign = 'right';
      ctx.font = '800 15px "Nunito Sans", sans-serif';
      ctx.fillStyle = 'rgba(' + THEME.jitterRGB + ',0.92)';
      ctx.fillText('Jitteriness Potential', a.right - 8, jTop + 20);
      ctx.fillStyle = 'rgba(' + THEME.tiredRGB + ',0.92)';
      ctx.fillText('Potential Tiredness', a.right - 8, tBot - 10);
      ctx.textAlign = 'left';
      ctx.fillStyle = 'rgba(31,27,22,0.62)';
      ctx.font = '800 13px "Nunito Sans", sans-serif';
      ctx.fillText('Baseline', a.left + 6, tTop - 7);

      // Active time marker.
      var px = x.getPixelForValue(lerp(MINUTES[Math.floor(pos)],
                MINUTES[Math.min(Math.floor(pos)+1, MINUTES.length-1)], pos - Math.floor(pos)));
      ctx.strokeStyle = 'rgba(31,27,22,0.22)';
      ctx.beginPath(); ctx.moveTo(px, a.top); ctx.lineTo(px, a.bottom); ctx.stroke();
      ctx.restore();
    },
    afterDatasetsDraw: function (c) {
      if (isNarrow()) return;
      var ctx = c.ctx, y = c.scales.y, x = c.scales.x;
      ctx.save();
      ANNOTATIONS.forEach(function (an) {
        if (pos < an.i) return;                       // not reached yet
        var di = ORDER.indexOf(an.series);
        if (c.getDatasetMeta(di).hidden) return;
        var px = x.getPixelForValue(MINUTES[an.i]);
        var py = y.getPixelForValue(SERIES[an.series].values[an.i]);
        ctx.textAlign = an.align === 'right' ? 'right' : 'left';
        var ox = an.align === 'right' ? -10 : 10;
        ctx.fillStyle = THEME[an.series].colour;
        ctx.font = '700 11.5px "Nunito Sans", sans-serif';
        ctx.fillText(an.main, px + ox, py - 12);
        if (an.sub) {
          ctx.fillStyle = 'rgba(31,27,22,0.5)';
          ctx.font = '400 10.5px "Nunito Sans", sans-serif';
          ctx.fillText(an.sub, px + ox, py + 2);
        }
      });
      ctx.restore();
    }
  };

  function ds(key) {
    var t = THEME[key];
    return {
      key: key, label: SERIES[key].label, data: visible(key),
      borderColor: t.colour, backgroundColor: t.colour, borderWidth: t.width,
      pointRadius: radii(key), pointHoverRadius: 6,
      pointBackgroundColor: t.colour, pointBorderColor: '#fff', pointBorderWidth: 2,
      tension: 0.4, cubicInterpolationMode: 'monotone', fill: false
    };
  }

  var chart = new Chart(canvas, {
    type: 'line',
    data: { datasets: ORDER.map(ds) },
    plugins: [decor],
    options: {
      responsive: true, maintainAspectRatio: false,
      animation: false,                              // we drive the motion ourselves
      interaction: { mode:'nearest', intersect:false, axis:'x' },
      scales: {
        x: {
          type:'linear', min:0, max:360,
          title:{ display:true, text:'Time After Drinking',
                  font:{ family:'"Nunito Sans", sans-serif', size:12, weight:'700' },
                  color:'rgba(31,27,22,0.55)' },
          afterBuildTicks: function (ax) { ax.ticks = MINUTES.map(function (m) { return { value:m }; }); },
          ticks:{ autoSkip:false, maxRotation:0,
                  callback:function (v) { var i = MINUTES.indexOf(v); return i < 0 ? '' : X_LABEL[i]; },
                  font:{ family:'"Nunito Sans", sans-serif', size:11 }, color:'rgba(31,27,22,0.5)' },
          grid:{ color:THEME.grid, drawTicks:false }
        },
        y: {
          min:Y_MIN, max:Y_MAX,
          title:{ display:true, text:'Perceived Energy',
                  font:{ family:'"Nunito Sans", sans-serif', size:12, weight:'700' },
                  color:'rgba(31,27,22,0.55)' },
          ticks:{ display:false }, grid:{ color:THEME.grid, drawTicks:false }
        }
      },
      plugins: {
        // Compact enough that all three labels hold one row inside the advertorial's
        // narrower text column, where the chart has ~640px rather than 900px.
        legend: { position:'top', align:'start',
          labels:{ usePointStyle:true, pointStyle:'circle', boxWidth:7, padding:9,
                   font:{ family:'"Nunito Sans", sans-serif', size:11, weight:'700' }, color:'#1F1B16' } },
        tooltip: {
          // Off by request: the dark hover card covered the curves it described.
          // The per-stage STORY panel below the chart carries the same content in
          // a place that does not obscure anything. Everything below is retained
          // so this is a one-word revert — flip to true to bring the card back.
          //
          // Note this is `enabled:false` rather than `events: []`. Emptying events
          // would also kill legend click-to-toggle, which the pulse and annotation
          // code depends on (both test meta.hidden).
          enabled: false,
          backgroundColor:'rgba(31,27,22,0.95)', cornerRadius:10, padding:12, displayColors:false,
          titleFont:{ family:'"Nunito Sans", sans-serif', size:13, weight:'800' },
          bodyFont:{ family:'"Nunito Sans", sans-serif', size:12 },
          callbacks:{
            title:function (items) {
              var m = Math.round(items[0].parsed.x), i = MINUTES.indexOf(m);
              return items[0].dataset.label + ' · ' + (i < 0 ? m + ' min' : X_LABEL[i]);
            },
            label:function () { return ''; },
            afterBody:function (items) {
              var t = TOOLTIPS[items[0].dataset.key] || {};
              return t[Math.round(items[0].parsed.x)] || t._ || '';
            }
          }
        }
      }
    }
  });

  var elPulse = root.querySelector('[data-ec-pulse]');

  // A CSS-animated element parked over MIMU's leading point. Repositioned on paint
  // rather than driven by its own rAF loop, so there is no permanent animation cost.
  function placePulse() {
    if (!elPulse) return;
    var di = ORDER.indexOf('mimu');
    var meta = chart.getDatasetMeta(di);
    var pts = meta && meta.data;
    if (!pts || !pts.length || meta.hidden) { elPulse.style.opacity = '0'; return; }
    var head = pts[pts.length - 1];
    // Chart.js reports coordinates relative to the CANVAS, but the pulse is
    // positioned against the wrapper's padding edge. Any padding on the wrapper
    // therefore offsets the marker, so add the canvas's own offset back in.
    elPulse.style.opacity = '1';
    elPulse.style.left = (canvas.offsetLeft + head.x) + 'px';
    elPulse.style.top = (canvas.offsetTop + head.y) + 'px';
  }

  function paint() {
    chart.data.datasets.forEach(function (d) {
      d.data = visible(d.key);
      d.pointRadius = radii(d.key);
    });
    chart.update('none');
    placePulse();
  }

  function renderStory() {
    var s = STORY[step];
    elStory.classList.add('is-fading');
    setTimeout(function () {
      elStory.innerHTML =
        '<p class="mimu-ec__story-h">' + s.h + '</p>' +
        '<ul class="mimu-ec__story-list">' +
        '<li><b style="color:' + THEME.mimu.colour + '">' + SERIES.mimu.label + '</b> ' + s.mimu + '</li>' +
        '<li><b style="color:' + THEME.coffee.colour + '">' + SERIES.coffee.label + '</b> ' + s.coffee + '</li>' +
        '<li><b style="color:' + THEME.cafe.colour + '">' + SERIES.cafe.label + '</b> ' + s.cafe + '</li>' +
        '</ul>';
      elStory.classList.remove('is-fading');
    }, reduced ? 0 : 180);

    Array.prototype.forEach.call(elDots.children, function (d, i) {
      d.classList.toggle('is-active', i === step);
    });
    if (elPrev) elPrev.disabled = step === 0;

    // At the last stage the only useful action is to watch it again, so the sole
    // control relabels itself rather than dead-ending as a disabled button.
    // Labels come from the markup so the copy stays in the template.
    var atEnd = step === MINUTES.length - 1;
    elNext.disabled = false;
    elNext.textContent = atEnd
      ? (elNext.getAttribute('data-label-restart') || 'Restart')
      : (elNext.getAttribute('data-label-next') || 'Next');
  }

  function goTo(target) {
    target = Math.max(0, Math.min(MINUTES.length - 1, target));
    if (target === step && pos === step) return;
    var from = pos, delta = target - from, t0 = null;
    step = target;
    renderStory();
    if (raf) cancelAnimationFrame(raf);
    if (reduced) { pos = target; paint(); return; }
    // A named function EXPRESSION: `frame` is in scope only inside its own body.
    // The IIFE self-schedules on the line above, so the stray
    // `raf = requestAnimationFrame(frame)` that used to sit after this block was
    // both redundant and a ReferenceError — it threw at the end of every goTo(),
    // which silently killed whatever the caller did next. That is what stopped
    // autoAdvance() from ever setting data-ec-hint.
    (function frame(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / THEME.duration);
      pos = from + delta * easeInOut(p);
      paint();
      if (p < 1) raf = requestAnimationFrame(frame);
    })(performance.now());
  }

  function stopPlay() {
    playing = false;
    clearTimeout(playTimer);
    if (elPlay) elPlay.textContent = 'Play';
  }

  function tickPlay() {
    if (!playing) return;
    if (step >= MINUTES.length - 1) return stopPlay();
    goTo(step + 1);
    playTimer = setTimeout(tickPlay, 1800);
  }

  elNext.addEventListener('click', function () {
    clearHint();
    stopPlay();
    goTo(step === MINUTES.length - 1 ? 0 : step + 1);
  });
  if (elPrev) elPrev.addEventListener('click', function () { clearHint(); stopPlay(); goTo(step - 1); });
  if (elReset) elReset.addEventListener('click', function () { clearHint(); stopPlay(); goTo(0); });
  if (elPlay) elPlay.addEventListener('click', function () {
    if (playing) return stopPlay();
    if (step >= MINUTES.length - 1) goTo(0);
    playing = true; elPlay.textContent = 'Pause';
    playTimer = setTimeout(tickPlay, 400);
  });

  Array.prototype.forEach.call(elDots.children, function (d, i) {
    d.addEventListener('click', function () { clearHint(); stopPlay(); goTo(i); });
  });

  // Swipe as an enhancement; tap remains primary.
  var sx = null;
  canvas.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive:true });
  canvas.addEventListener('touchend', function (e) {
    if (sx === null) return;
    var dx = e.changedTouches[0].clientX - sx;
    if (Math.abs(dx) > 50) { clearHint(); stopPlay(); goTo(step + (dx < 0 ? 1 : -1)); }
    sx = null;
  }, { passive:true });

  // The chart was being scrolled past as if it were a static image. Advancing one
  // step the first time it comes into view makes the curves move before the reader
  // touches anything, which is what signals it is interactive. Everything after
  // the first hour stays manual.
  //
  // data-ec-hint is the CSS hook for the glow on Next. It is set once the auto
  // step lands and cleared by the reader's first interaction of any kind, so the
  // cue never competes with someone already using the control.
  var autoDone = false;

  function clearHint() {
    root.removeAttribute('data-ec-hint');
  }

  function autoAdvance() {
    if (autoDone) return;
    autoDone = true;
    if (step === 0) goTo(1);
    root.setAttribute('data-ec-hint', '');
  }

  renderStory();
  paint();

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          // A beat before it moves, so the reader registers the starting state
          // rather than arriving after the change has already happened.
          setTimeout(autoAdvance, 450);
          return;
        }
      }
    }, { threshold: 0.25 });
    io.observe(root);
  } else {
    // No IntersectionObserver: still advance, so the cue is never withheld.
    autoAdvance();
  }
})();
